/**
 * Adapted from Identicon.js 2.3.3 (Derk-Jan Karrenbeld)
 * http://github.com/stewartlord/identicon.js
 *
 * Copyright 2018, Stewart Lord
 * Released under the BSD license
 * http://www.opensource.org/licenses/bsd-license.php
 */

type Options = {
  background: [number, number, number] | [number, number, number, number];
  margin: number;
  size: number;
  saturation: number;
  brightness: number;
  format: 'svg';
  foreground?: [number, number, number] | [number, number, number, number];
};

export class Identicon {
  private readonly options: Options;

  private readonly hash: string;
  private readonly foreground:
    [number, number, number] | [number, number, number, number];

  constructor(hash: string, options?: Partial<Options>) {
    if (typeof hash !== 'string' || hash.length < 15) {
      throw new Error('A hash of at least 15 characters is required.');
    }

    const defaults: Options = {
      background: [240, 240, 240, 255],
      margin: 0.08,
      size: 64,
      saturation: 0.7,
      brightness: 0.5,
      format: 'svg',
    };

    this.options = { ...defaults, ...options };

    this.hash = hash;

    // foreground defaults to last 7 chars as hue at 70% saturation, 50% brightness
    const hue = parseInt(this.hash.substring(-7), 16) / 0xfffffff;
    const saturation = this.options.saturation;
    const brightness = this.options.brightness;

    this.foreground =
      this.options.foreground || this.hsl2rgb(hue, saturation, brightness);
  }

  image() {
    return new SimpleSvg(
      this.options.size,
      this.foreground,
      this.options.background,
    );
  }

  render() {
    const image = this.image(),
      size = this.options.size,
      baseMargin = Math.floor(size * this.options.margin),
      cell = Math.floor((size - baseMargin * 2) / 5),
      margin = Math.floor((size - cell * 5) / 2),
      bg = image.color.apply(image, this.options.background),
      fg = image.color.apply(image, this.foreground);

    // the first 15 characters of the hash control the pixels (even/odd)
    // they are drawn down the middle first, then mirrored outwards
    for (let i = 0; i < 15; i++) {
      const color = parseInt(this.hash.charAt(i), 16) % 2 ? bg : fg;

      if (i < 5) {
        this.rectangle(
          2 * cell + margin,
          i * cell + margin,
          cell,
          cell,
          color,
          image,
        );
      } else if (i < 10) {
        this.rectangle(
          1 * cell + margin,
          (i - 5) * cell + margin,
          cell,
          cell,
          color,
          image,
        );
        this.rectangle(
          3 * cell + margin,
          (i - 5) * cell + margin,
          cell,
          cell,
          color,
          image,
        );
      } else if (i < 15) {
        this.rectangle(
          0 * cell + margin,
          (i - 10) * cell + margin,
          cell,
          cell,
          color,
          image,
        );
        this.rectangle(
          4 * cell + margin,
          (i - 10) * cell + margin,
          cell,
          cell,
          color,
          image,
        );
      }
    }

    return image;
  }

  rectangle(
    x: number,
    y: number,
    w: number,
    h: number,
    color: string,
    image: SimpleSvg,
  ) {
    return image.rectangles.push({ x: x, y: y, w: w, h: h, color: color });
  }

  // adapted from: https://gist.github.com/aemkei/1325937
  hsl2rgb(h: number, s: number, b: number) {
    h *= 6;

    const d = [
      (b += s *= b < 0.5 ? b : 1 - b),
      b - (h % 1) * s * 2,
      (b -= s *= 2),
      b,
      b + (h % 1) * s,
      b + s,
    ];

    return [
      d[~~h % 6] * 255, // red
      d[(h | 16) % 6] * 255, // green
      d[(h | 8) % 6] * 255, // blue
    ] as [number, number, number];
  }

  toString(raw?: boolean) {
    if (raw) {
      return this.render().getDump();
    } else {
      return this.render().getBase64();
    }
  }
}

type Rectangle = {
  color: string;
  w: number;
  h: number;
  x: number;
  y: number;
};

class SimpleSvg {
  private readonly foreground: string;
  private readonly background: string;
  private readonly size: number;

  rectangles: Rectangle[];

  constructor(
    size: number,
    foreground: [number, number, number] | [number, number, number, number],
    background: [number, number, number] | [number, number, number, number],
  ) {
    this.size = size;
    this.foreground = this.color.apply(this, foreground);
    this.background = this.color.apply(this, background);
    this.rectangles = [];
  }

  color(r: number, g: number, b: number, a: number = -1) {
    const values = [r, g, b].map(Math.round);
    values.push(a >= 0 && a <= 255 ? a / 255 : 1);
    return 'rgba(' + values.join(',') + ')';
  }

  getDump() {
    let i, xml, rect;

    const fg = this.foreground,
      bg = this.background,
      stroke = this.size * 0.005;

    xml =
      "<svg xmlns='http://www.w3.org/2000/svg'" +
      " width='" +
      this.size +
      "' height='" +
      this.size +
      "'" +
      " style='background-color:" +
      bg +
      ";'>" +
      "<g style='fill:" +
      fg +
      '; stroke:' +
      fg +
      '; stroke-width:' +
      stroke +
      ";'>";

    for (i = 0; i < this.rectangles.length; i++) {
      rect = this.rectangles[i];
      if (rect.color == bg) continue;
      xml +=
        '<rect ' +
        " x='" +
        rect.x +
        "'" +
        " y='" +
        rect.y +
        "'" +
        " width='" +
        rect.w +
        "'" +
        " height='" +
        rect.h +
        "'" +
        '/>';
    }
    xml += '</g></svg>';

    return xml;
  }

  getBase64() {
    if ('function' === typeof btoa) {
      return btoa(this.getDump());
    } else if ('Buffer' in globalThis) {
      return Buffer.from(this.getDump(), 'binary').toString('base64');
    } else {
      throw new Error('Cannot generate base64 output');
    }
  }
}
